import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { getValueFromPosition } from '@/utils';
import { SliderMark } from './slider-marker';
import { SliderThumb } from './slider-thumb';
import { SliderTrack } from './slider-track';
export var SliderBase = function (_a) {
    var min = _a.min, max = _a.max, _b = _a.step, step = _b === void 0 ? 1 : _b, value = _a.value, onChange = _a.onChange, marks = _a.marks, _c = _a.showTooltip, initialShowTooltip = _c === void 0 ? false : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.size, size = _e === void 0 ? 'md' : _e, _f = _a.className, className = _f === void 0 ? '' : _f, _g = _a.tooltipSuffix, tooltipSuffix = _g === void 0 ? '' : _g;
    var trackRef = useRef(null);
    var _h = useState(null), dragging = _h[0], setDragging = _h[1];
    var _j = useState(null), focused = _j[0], setFocused = _j[1];
    var _k = useState(initialShowTooltip), showTooltip = _k[0], setShowTooltip = _k[1];
    var dragStartRef = useRef();
    var isRange = Array.isArray(value);
    var _l = isRange ? value : [min, value], startValue = _l[0], endValue = _l[1];
    var sizeClasses = {
        sm: 'h-4',
        md: 'h-6',
        lg: 'h-8',
    };
    var marginClasses = {
        sm: 'mt-2',
        md: 'mt-3',
        lg: 'mt-4',
    };
    var markerFontSize = {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
    };
    var handleTrackClick = function (e) {
        if (disabled || !trackRef.current || dragging)
            return;
        var rect = trackRef.current.getBoundingClientRect();
        var position = e.clientX - rect.left;
        var newValue = getValueFromPosition(position, rect, min, max, step);
        if (isRange) {
            // For range slider, move the closest thumb
            var startDiff = Math.abs(newValue - startValue);
            var endDiff = Math.abs(newValue - endValue);
            if (startDiff < endDiff) {
                onChange([Math.min(newValue, endValue), endValue]);
            }
            else {
                onChange([startValue, Math.max(newValue, startValue)]);
            }
        }
        else {
            onChange(newValue);
        }
    };
    var handleThumbMouseDown = function (position) { return function (e) {
        e.stopPropagation();
        if (disabled)
            return;
        setDragging(position);
        dragStartRef.current = {
            x: e.clientX,
            value: isRange ? (position === 'start' ? startValue : endValue) : endValue,
        };
        // Set focus when thumb is clicked
        setFocused(position);
    }; };
    var handleThumbKeyDown = function (position) { return function (e) {
        if (disabled)
            return;
        var increment = e.shiftKey ? step * 10 : step;
        var newValue = position === 'start' ? startValue : endValue;
        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                newValue = Math.max(position === 'start' ? min : startValue, newValue - increment);
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                newValue = Math.min(position === 'start' ? endValue : max, newValue + increment);
                break;
            default:
                return;
        }
        if (isRange) {
            onChange(position === 'start' ? [newValue, endValue] : [startValue, newValue]);
        }
        else {
            onChange(newValue);
        }
    }; };
    useEffect(function () {
        if (!dragging || !dragStartRef.current)
            return;
        var handleMouseMove = function (e) {
            if (!trackRef.current || !dragStartRef.current)
                return;
            var rect = trackRef.current.getBoundingClientRect();
            var position = e.clientX - rect.left;
            var newValue = getValueFromPosition(position, rect, min, max, step);
            if (isRange) {
                if (dragging === 'start') {
                    onChange([Math.min(newValue, endValue), endValue]);
                }
                else {
                    onChange([startValue, Math.max(newValue, startValue)]);
                }
            }
            else {
                onChange(newValue);
            }
        };
        var handleMouseUp = function () {
            setDragging(null);
            dragStartRef.current = undefined;
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return function () {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging, isRange, startValue, endValue, onChange, min, max, step]);
    var startPercentage = ((startValue - min) / (max - min)) * 100;
    var endPercentage = ((endValue - min) / (max - min)) * 100;
    return (_jsxs("div", { className: "relative w-full ".concat(sizeClasses[size], " ").concat(className), children: [_jsxs("div", { className: 'absolute inset-0 translate-y-1/2 cursor-pointer z-50', onClick: handleTrackClick, ref: trackRef, children: [_jsx(SliderTrack, { className: 'absolute inset-0' }), _jsx(SliderTrack, { className: 'absolute inset-0', filled: true, disabled: disabled, style: {
                            left: "".concat(startPercentage, "%"),
                            right: "".concat(100 - endPercentage, "%"),
                        } })] }), marks && (_jsx("div", { className: 'absolute inset-x-0 top-1/2 translate-y-1/2', children: marks.map(function (_a) {
                    var markValue = _a.value, label = _a.label;
                    return (_jsx(SliderMark, { value: ((markValue - min) / (max - min)) * 100, label: label, active: markValue >= startValue && markValue <= endValue, labelClassName: markValue === max
                            ? "".concat(marginClasses[size], " ").concat(markerFontSize[size], "  relative right-full")
                            : markValue === min
                                ? "".concat(marginClasses[size], " ").concat(markerFontSize[size], " relative left-0")
                                : "".concat(marginClasses[size], " ").concat(markerFontSize[size], " relative -translate-x-1/2") }, markValue));
                }) })), isRange && (_jsx("div", { className: 'relative', children: _jsx(SliderThumb, { disabled: disabled, focused: focused === 'start', showTooltip: showTooltip, value: startValue, tooltipSuffix: tooltipSuffix, onMouseEnter: function () { return setShowTooltip(true); }, onMouseLeave: function () { return setShowTooltip(false); }, onMouseDown: handleThumbMouseDown('start'), onKeyDown: handleThumbKeyDown('start'), tabIndex: disabled ? -1 : 0, size: size, left: "".concat(startPercentage, "%") }) })), _jsx("div", { className: 'relative', children: _jsx(SliderThumb, { disabled: disabled, focused: focused === 'end', showTooltip: showTooltip, value: endValue, tooltipSuffix: tooltipSuffix, onMouseEnter: function () { return setShowTooltip(true); }, onMouseLeave: function () { return setShowTooltip(false); }, onMouseDown: handleThumbMouseDown('end'), onKeyDown: handleThumbKeyDown('end'), tabIndex: disabled ? -1 : 0, size: size, left: "".concat(endPercentage, "%") }) })] }));
};
